import { IPageDescription } from '@wix/adi-content-api';
import {
  CaasContentProvider,
  AiContentProvider,
  UserData,
} from '@wix/editor-content-provider';
import {
  ContentKind,
  getContentCategoryByCeType,
  GetContentOptions,
  PageData,
} from '@wix/editor-content-provider';
import { CompStructure } from '@wix/document-services-types';
import { PromptsOverrideParams } from '../types';
import { ALLOWED_HEADLESS_INSTALL_APP_DEF_IDS } from '../consts';

type ContentProviderType = Omit<
  CaasContentProvider,
  | 'initContent'
  | 'getIsCaasInitialized'
  | 'getPageSectionsContents'
  | 'getAdditionalPagesData'
>;

export class ContentProvider implements ContentProviderType {
  aiContentProvider?: AiContentProvider;
  caasContentProvider: CaasContentProvider;
  private allowedApps: Set<string> = new Set();

  constructor({
    origin,
    locale,
    contentVersion,
    useAiContentProvider = false,
    promptsOverride,
    metasiteInstance,
    ...userData
  }: {
    origin?: string;
    locale: string;
    contentVersion?: string;
    useAiContentProvider?: boolean;
    metasiteInstance: string;
    promptsOverride?: PromptsOverrideParams;
  } & UserData) {
    this.caasContentProvider = new CaasContentProvider({
      origin,
      locale,
      contentVersion,
    });
    if (useAiContentProvider) {
      this.aiContentProvider = new AiContentProvider({
        businessTypeName: userData.businessTypeName,
        businessName: userData.businessName,
        description: userData.description,
        language: locale,
        promptsOverride,
        metasiteInstance,
      });
    }
  }

  initContent({
    industryId,
    structureId,
    businessTypeName,
    appDefIds,
  }: {
    businessTypeName: string;
    industryId?: string;
    structureId?: string;
    appDefIds: string[];
  }) {
    if (this.aiContentProvider) {
      this.aiContentProvider.setBusinessTypeName(businessTypeName);
      this.aiContentProvider.init(appDefIds);
    }
    return this.caasContentProvider.initContent(industryId, structureId);
  }

  getSuggestedKits() {
    return this.caasContentProvider.getSuggestedKits();
  }

  getLayoutFamilyBlacklist() {
    return this.caasContentProvider.getLayoutFamilyBlacklist();
  }

  setUserData(userData: UserData) {
    this.caasContentProvider.setUserData(userData);
    if (this.aiContentProvider) {
      this.aiContentProvider.setBusinessTypeName(userData.businessTypeName);
    }
  }

  getInitialHomepageData() {
    if (this.aiContentProvider) {
      return this.aiContentProvider.getInitialHomepageData();
    }
    return this.caasContentProvider.getInitialHomepageData();
  }

  async getHomepageData(
    appDefIds: string[],
    appDefIdsNotToPersonalize: string[],
    initialHomepageData: IPageDescription,
  ): Promise<PageData> {
    if (this.aiContentProvider) {
      // Need to wait until caas is fully initialized
      await this.caasContentProvider.getIsCaasInitialized();
      const sections = this.caasContentProvider.getPageSectionsContents(
        initialHomepageData.sections,
        appDefIds,
        appDefIdsNotToPersonalize,
        false,
      );
      this.allowedApps = new Set([
        ...appDefIds,
        ...appDefIdsNotToPersonalize,
        ...ALLOWED_HEADLESS_INSTALL_APP_DEF_IDS,
      ]);
      return {
        name: initialHomepageData.name,
        sections: sections
          .filter(
            (section) =>
              !section.appDefId || this.allowedApps.has(section.appDefId),
          )
          .map((section) => ({
            ...section,
            contentCategory: getContentCategoryByCeType(section.ceType),
          })),
      };
    }
    return this.caasContentProvider.getHomepageData(
      appDefIds,
      appDefIdsNotToPersonalize,
      initialHomepageData,
    );
  }

  initAdditionalPagesData() {
    if (this.aiContentProvider) {
      return this.aiContentProvider.initAdditionalPagesData();
    }
    return this.caasContentProvider.initAdditionalPagesData();
  }

  async getAdditionalPagesData(
    additionalPages: IPageDescription[],
  ): Promise<PageData[]> {
    const additionalPagesData =
      await this.caasContentProvider.getAdditionalPagesData(additionalPages);
    if (this.aiContentProvider) {
      return additionalPagesData.map((pageData) => ({
        ...pageData,
        sections: pageData.sections
          .filter(
            (section) =>
              !section.appDefId || this.allowedApps.has(section.appDefId),
          )
          .map((section) => ({
            ...section,
            contentCategory: getContentCategoryByCeType(section.ceType),
          })),
      }));
    }
    return additionalPagesData;
  }

  getContent(
    contentOptions: GetContentOptions,
    compStructure: CompStructure,
    contentKind: ContentKind = ContentKind.ALL,
  ) {
    return this.caasContentProvider.getContent(
      contentOptions,
      compStructure,
      contentKind,
    );
  }

  getIndustryProfiles() {
    return this.caasContentProvider.getIndustryProfiles();
  }

  setProfileId(profileId: string) {
    return this.caasContentProvider.setProfileId(profileId);
  }

  getCurrentProfile() {
    return this.caasContentProvider.getCurrentProfile();
  }

  getBusinessTypeByTerm(term: string) {
    return this.caasContentProvider.getBusinessTypeByTerm(term);
  }

  getAppsInstallOptions(appDefIds: string[]) {
    return this.caasContentProvider.getAppsInstallOptions(appDefIds);
  }
}
